import { useState, useEffect } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useAuth } from "@workspace/admin-ui";
import { PageHeader } from "@/components/AppShell";
import { chainAdminApi } from "@/lib/callables";
import { useToast } from "@/lib/toast";

// ── Types ────────────────────────────────────────────────────────────────

type AvailabilityWindow = {
  dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  startTime: string;
  endTime: string;
};

type BlockoutDate = {
  date: string;
  reason?: string;
};

const DAY_NAMES = [
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
];

// ── Main page ────────────────────────────────────────────────────────────

export default function RoomsAvailabilityPharmacyDetailPage() {
  const { membership } = useAuth();
  const chainId = membership?.chainId ?? "";
  const params = useParams<{ pharmacyId: string }>();
  const pharmacyId = params.pharmacyId;

  const [selectedBookingPharmacyId, setSelectedBookingPharmacyId] = useState<string | null>(null);
  const [selectedPharmacistId, setSelectedPharmacistId] = useState<string | null>(null);
  const [editingAvailabilityForId, setEditingAvailabilityForId] = useState<string | null>(null);

  // 1. Overview — find if we have one or multiple booking pharmacies
  const overviewQ = useQuery({
    queryKey: ["bookings", "overview", chainId],
    queryFn: () => chainAdminApi.chainAdminBookingsOverview({ chainId }),
    enabled: !!chainId,
  });

  const pharmacyOverview = overviewQ.data?.items.find((i) => i.pharmacyId === pharmacyId);

  // 2. If multiple locations, list them
  const listBookingPharmaciesQ = useQuery({
    queryKey: ["bookings", "pharmacies", chainId, pharmacyId],
    queryFn: () => chainAdminApi.chainAdminListBookingPharmacies({ chainId, pharmacyId }),
    enabled: !!chainId && !!pharmacyOverview && pharmacyOverview.bookingPharmacyCount > 1,
  });

  const bookingPharmacyId =
    selectedBookingPharmacyId ||
    (pharmacyOverview?.bookingPharmacyCount === 1 ? pharmacyOverview.bookingPharmacyId : null);

  // 3. Rooms
  const roomsQ = useQuery({
    queryKey: ["bookings", "rooms", chainId, bookingPharmacyId],
    queryFn: () =>
      chainAdminApi.chainAdminListRooms({ chainId, bookingPharmacyId: bookingPharmacyId! }),
    enabled: !!chainId && !!bookingPharmacyId,
  });

  // 4. Pharmacists with availability
  const pharmacistsQ = useQuery({
    queryKey: ["bookings", "availability-pharmacists", chainId, bookingPharmacyId],
    queryFn: () =>
      chainAdminApi.chainAdminListAvailabilityPharmacists({
        chainId,
        bookingPharmacyId: bookingPharmacyId!,
      }),
    enabled: !!chainId && !!bookingPharmacyId,
  });

  // 5. Availability for selected pharmacist
  const availabilityQ = useQuery({
    queryKey: ["bookings", "availability", chainId, bookingPharmacyId, selectedPharmacistId],
    queryFn: () =>
      chainAdminApi.chainAdminGetAvailability({
        chainId,
        bookingPharmacyId: bookingPharmacyId!,
        pharmacistId: selectedPharmacistId!,
      }),
    enabled: !!chainId && !!bookingPharmacyId && !!selectedPharmacistId,
  });

  const pharmacists = pharmacistsQ.data?.items ?? [];

  if (overviewQ.isLoading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (!pharmacyOverview) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Pharmacy not found in bookings overview.</Typography>
        <Link href="/rooms">Back to Rooms &amp; Availability</Link>
      </Box>
    );
  }

  const editingPharmacist = pharmacists.find((p) => p.pharmacistId === editingAvailabilityForId);

  return (
    <>
      <PageHeader
        title={pharmacyOverview.displayName}
        description={`Rooms & availability for pharmacy ${pharmacyId}`}
        actions={
          <Button
            component={Link}
            href="/rooms"
            startIcon={<ChevronLeftIcon />}
            variant="outlined"
            size="small"
          >
            Back to Rooms &amp; Availability
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Location picker (multi-location pharmacies) */}
        {pharmacyOverview.bookingPharmacyCount > 1 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Select Booking Location
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              More than one booking record exists for this pharmacy. The one marked
              "Primary" is the record the patient booking link and the mobile app's
              own availability writes resolve to — pick it unless you specifically
              need to inspect an older, retired one.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {listBookingPharmaciesQ.data?.items.map((bp) => (
                <Chip
                  key={bp.id}
                  label={`${bp.name} (${bp.eircode})${bp.isPrimary ? " · Primary" : ""}`}
                  onClick={() => {
                    setSelectedBookingPharmacyId(bp.id);
                    setSelectedPharmacistId(null);
                  }}
                  color={bookingPharmacyId === bp.id ? "primary" : bp.isPrimary ? "success" : "default"}
                  variant={bookingPharmacyId === bp.id ? "filled" : "outlined"}
                />
              ))}
            </Box>
          </Paper>
        )}

        {!bookingPharmacyId ? (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">Please select a booking location.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {/* Left panel — Rooms + Pharmacists */}
            <Grid size={{ xs: 12, md: 4 }}>
              {/* Rooms */}
              <Paper sx={{ mb: 3 }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Rooms
                  </Typography>
                </Box>
                {roomsQ.isLoading ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading rooms…
                    </Typography>
                  </Box>
                ) : !roomsQ.data?.items.length ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No rooms configured.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1 }}>
                    {roomsQ.data.items.map((room) => (
                      <Card key={room.id} variant="outlined" sx={{ mb: 1 }}>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {room.name}
                            </Typography>
                            <Chip
                              label={room.isActive ? "Active" : "Inactive"}
                              size="small"
                              color={room.isActive ? "success" : "default"}
                              variant="outlined"
                            />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {room.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>

              {/* Pharmacists */}
              <Paper>
                <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Pharmacists
                  </Typography>
                </Box>
                {pharmacistsQ.isLoading ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading pharmacists…
                    </Typography>
                  </Box>
                ) : !pharmacists.length ? (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      No pharmacist has set up their availability schedule yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1 }}>
                    {pharmacists.map((p) => (
                      <Card
                        key={p.pharmacistId}
                        variant="outlined"
                        sx={{
                          mb: 1,
                          cursor: "pointer",
                          borderColor:
                            selectedPharmacistId === p.pharmacistId ? "primary.main" : "divider",
                          bgcolor:
                            selectedPharmacistId === p.pharmacistId ? "primary.50" : "inherit",
                        }}
                        onClick={() => setSelectedPharmacistId(p.pharmacistId)}
                      >
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {p.displayName}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right panel — Pharmacist Availability */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper sx={{ height: "100%" }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {selectedPharmacistId
                      ? `Availability: ${pharmacists.find((p) => p.pharmacistId === selectedPharmacistId)?.displayName}`
                      : "Select a pharmacist to view availability"}
                  </Typography>
                  {selectedPharmacistId && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => setEditingAvailabilityForId(selectedPharmacistId)}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
                <Box sx={{ p: 2 }}>
                  {!selectedPharmacistId ? (
                    <Typography variant="body2" color="text.secondary">
                      Select a pharmacist on the left to see their schedule.
                    </Typography>
                  ) : availabilityQ.isLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading availability…
                    </Typography>
                  ) : !availabilityQ.data?.availability ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No availability configured for this pharmacist yet.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setEditingAvailabilityForId(selectedPharmacistId)}
                      >
                        Set Up Availability
                      </Button>
                    </Box>
                  ) : (
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          gutterBottom
                          sx={{ display: "block" }}
                        >
                          Weekly Schedule
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Day</TableCell>
                                <TableCell>Hours</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {DAY_NAMES.map((day, idx) => {
                                const windows =
                                  availabilityQ.data!.availability!.windows.filter(
                                    (w) => w.dayOfWeek === idx,
                                  );
                                return (
                                  <TableRow key={day}>
                                    <TableCell sx={{ fontWeight: 500 }}>{day}</TableCell>
                                    <TableCell>
                                      {windows.length > 0 ? (
                                        windows.map((w, i) => (
                                          <Chip
                                            key={i}
                                            label={`${w.startTime} - ${w.endTime}`}
                                            size="small"
                                            sx={{ mr: 0.5 }}
                                          />
                                        ))
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">
                                          Closed
                                        </Typography>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>

                      {availabilityQ.data.availability.blockoutDates.length > 0 && (
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            gutterBottom
                            sx={{ display: "block" }}
                          >
                            Blockout Dates
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Date</TableCell>
                                  <TableCell>Reason</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {availabilityQ.data.availability.blockoutDates.map((bo, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>{bo.date}</TableCell>
                                    <TableCell>{bo.reason || "—"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Stack>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      {/* Availability edit dialog */}
      {editingAvailabilityForId && bookingPharmacyId && (
        <Dialog
          open={!!editingAvailabilityForId}
          onClose={() => setEditingAvailabilityForId(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Manage Availability —{" "}
            {editingPharmacist?.displayName ?? editingAvailabilityForId}
          </DialogTitle>
          <AvailabilityEditForm
            key={editingAvailabilityForId}
            chainId={chainId}
            bookingPharmacyId={bookingPharmacyId}
            pharmacistId={editingAvailabilityForId}
            onClose={() => setEditingAvailabilityForId(null)}
          />
        </Dialog>
      )}
    </>
  );
}

// ── Availability edit form ────────────────────────────────────────────────

function AvailabilityEditForm({
  chainId,
  bookingPharmacyId,
  pharmacistId,
  onClose,
}: {
  chainId: string;
  bookingPharmacyId: string;
  pharmacistId: string;
  onClose: () => void;
}) {
  const { showError, showSuccess } = useToast();
  const qc = useQueryClient();

  const availQ = useQuery({
    queryKey: ["bookings", "availability", chainId, bookingPharmacyId, pharmacistId],
    queryFn: () =>
      chainAdminApi.chainAdminGetAvailability({ chainId, bookingPharmacyId, pharmacistId }),
  });

  const [windows, setWindows] = useState<AvailabilityWindow[]>([]);
  const [blockoutDates, setBlockoutDates] = useState<BlockoutDate[]>([]);

  const [newWinDay, setNewWinDay] = useState<number>(1);
  const [newWinStart, setNewWinStart] = useState("09:00");
  const [newWinEnd, setNewWinEnd] = useState("17:00");

  const [newBODate, setNewBODate] = useState("");
  const [newBOReason, setNewBOReason] = useState("");

  const [confirmingWindowIdx, setConfirmingWindowIdx] = useState<number | null>(null);
  const [confirmingBlockoutIdx, setConfirmingBlockoutIdx] = useState<number | null>(null);

  useEffect(() => {
    if (availQ.data !== undefined) {
      setWindows(availQ.data.availability?.windows ?? []);
      setBlockoutDates(availQ.data.availability?.blockoutDates ?? []);
    }
  }, [availQ.data]);

  const saveM = useMutation({
    mutationFn: () =>
      chainAdminApi.chainAdminSetAvailability({
        chainId,
        bookingPharmacyId,
        pharmacistId,
        windows,
        blockoutDates,
      }),
    onSuccess: () => {
      showSuccess("Availability saved");
      void qc.invalidateQueries({
        queryKey: ["bookings", "availability", chainId, bookingPharmacyId, pharmacistId],
      });
      onClose();
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : "Failed to save availability"),
  });

  const addWindow = () => {
    if (!newWinStart || !newWinEnd) return;
    if (newWinEnd <= newWinStart) {
      showError("End time must be after start time");
      return;
    }
    setWindows((prev) => [
      ...prev,
      {
        dayOfWeek: newWinDay as AvailabilityWindow["dayOfWeek"],
        startTime: newWinStart,
        endTime: newWinEnd,
      },
    ]);
  };

  const removeWindow = (i: number) => {
    setWindows((prev) => prev.filter((_, j) => j !== i));
    setConfirmingWindowIdx(null);
  };

  const addBlockout = () => {
    if (!newBODate) return;
    setBlockoutDates((prev) => [
      ...prev,
      { date: newBODate, ...(newBOReason ? { reason: newBOReason } : {}) },
    ]);
    setNewBODate("");
    setNewBOReason("");
  };

  const removeBlockout = (i: number) => {
    setBlockoutDates((prev) => prev.filter((_, j) => j !== i));
    setConfirmingBlockoutIdx(null);
  };

  if (availQ.isLoading || availQ.data === undefined) {
    return (
      <>
        <DialogContent>
          <Typography color="text.secondary">Loading current schedule…</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={4}>
          {/* Weekly windows */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Weekly Windows
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {windows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="caption" color="text.secondary">
                          No recurring windows — add one below.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {windows.map((w, i) => (
                    <TableRow key={i}>
                      <TableCell>{DAY_NAMES[w.dayOfWeek]}</TableCell>
                      <TableCell>{w.startTime}</TableCell>
                      <TableCell>{w.endTime}</TableCell>
                      <TableCell align="right">
                        {confirmingWindowIdx === i ? (
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => removeWindow(i)}
                            >
                              Remove
                            </Button>
                            <Button size="small" onClick={() => setConfirmingWindowIdx(null)}>
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <IconButton size="small" onClick={() => setConfirmingWindowIdx(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Day</InputLabel>
                <Select
                  value={newWinDay}
                  label="Day"
                  onChange={(e) => setNewWinDay(Number(e.target.value))}
                >
                  {DAY_NAMES.map((d, i) => (
                    <MenuItem key={i} value={i}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Start"
                type="time"
                size="small"
                value={newWinStart}
                onChange={(e) => setNewWinStart(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 120 }}
              />
              <TextField
                label="End"
                type="time"
                size="small"
                value={newWinEnd}
                onChange={(e) => setNewWinEnd(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 120 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addWindow}
              >
                Add Window
              </Button>
            </Box>
          </Box>

          <Divider />

          {/* Blockout dates */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Blockout Dates
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {blockoutDates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="caption" color="text.secondary">
                          No blockout dates — add one below.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {blockoutDates.map((b, i) => (
                    <TableRow key={i}>
                      <TableCell>{b.date}</TableCell>
                      <TableCell>{b.reason || "—"}</TableCell>
                      <TableCell align="right">
                        {confirmingBlockoutIdx === i ? (
                          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => removeBlockout(i)}
                            >
                              Remove
                            </Button>
                            <Button size="small" onClick={() => setConfirmingBlockoutIdx(null)}>
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <IconButton size="small" onClick={() => setConfirmingBlockoutIdx(i)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
              <TextField
                label="Date"
                type="date"
                size="small"
                value={newBODate}
                onChange={(e) => setNewBODate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: 160 }}
              />
              <TextField
                label="Reason (optional)"
                size="small"
                value={newBOReason}
                onChange={(e) => setNewBOReason(e.target.value)}
                sx={{ width: 220 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addBlockout}
              >
                Add Date
              </Button>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saveM.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => saveM.mutate()}
          disabled={saveM.isPending}
        >
          {saveM.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </>
  );
}
